import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useMediaQuery, useStorage } from '@vueuse/core'
import type { UiMode, Locale } from '@/types'

export const useUiStore = defineStore('ui', () => {
  // Persistent state
  const mode = useStorage<UiMode>('diabassist-mode', 'classic')
  const locale = useStorage<Locale>('diabassist-locale', 'fr')
  const isDark = useStorage('diabassist-dark', false)

  // Reactive state
  const sidebarOpen = ref(true)
  const isMobile = useMediaQuery('(max-width: 768px)')
  const isTablet = useMediaQuery('(max-width: 1024px)')

  // Getters
  const isRtl = computed(() => locale.value === 'ar')
  const isWordless = computed(() => mode.value === 'wordless')
  const isIntent = computed(() => mode.value === 'intent')
  const isClassic = computed(() => mode.value === 'classic')

  // Auto-close sidebar on mobile
  watch(isMobile, (mobile) => {
    if (mobile) {
      sidebarOpen.value = false
    }
  })

  // Actions
  function setMode(newMode: UiMode) {
    mode.value = newMode
  }

  function setLocale(newLocale: Locale) {
    locale.value = newLocale
    // Update document direction
    document.documentElement.dir = newLocale === 'ar' ? 'rtl' : 'ltr'
    document.documentElement.lang = newLocale
  }

  function toggleDark() {
    isDark.value = !isDark.value
    // Update document class
    if (isDark.value) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  function toggleSidebar() {
    sidebarOpen.value = !sidebarOpen.value
  }

  function openSidebar() {
    sidebarOpen.value = true
  }

  function closeSidebar() {
    sidebarOpen.value = false
  }

  // Initialize dark mode from storage
  function initTheme() {
    if (isDark.value) {
      document.documentElement.classList.add('dark')
    }
    document.documentElement.dir = locale.value === 'ar' ? 'rtl' : 'ltr'
    document.documentElement.lang = locale.value
  }

  return {
    // State
    mode,
    locale,
    isDark,
    sidebarOpen,
    isMobile,
    isTablet,
    // Getters
    isRtl,
    isWordless,
    isIntent,
    isClassic,
    // Actions
    setMode,
    setLocale,
    toggleDark,
    toggleSidebar,
    openSidebar,
    closeSidebar,
    initTheme,
  }
})
